<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { stockDailyApi, tradeCalendarApi } from '@/api/stock'
import { useEChart } from '@/composables/useEChart'
import StockFilterPanel from '@/components/stock/StockFilterPanel.vue'
import PageHero from '@/components/common/PageHero.vue'

const { chartRef, instance, init } = useEChart()
const router = useRouter()

const loading = ref(false)
const latestTradeDate = ref('')
const total = ref(0)

const filterForm = ref({
  tradeDate: '',
  symbol: '',
  minMarketCap: 100
})

function getThemeColor (name, fallback) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

function buildChartOption (data) {
  const isDark = document.documentElement.dataset.theme !== 'light'
  const textColor = getThemeColor('--text-muted', isDark ? '#a1a1aa' : '#495057')
  const primaryTextColor = getThemeColor('--text-primary', isDark ? '#f5f5f5' : '#212529')
  const ruleColor = getThemeColor('--rule', isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')
  const accentColor = getThemeColor('--accent', '#3b82f6')

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: isDark ? 'rgba(17, 17, 17, 0.95)' : 'rgba(255,255,255,0.98)',
      borderColor: ruleColor,
      textStyle: { color: primaryTextColor, fontSize: 12 },
      formatter: (params) => {
        const item = data[params[0].dataIndex]
        return `${item.label}<br/>股票数: ${item.count} 只`
      }
    },
    grid: { left: 42, right: 24, top: 28, bottom: 42 },
    xAxis: {
      type: 'category',
      data: data.map(item => item.label),
      axisLabel: {
        color: textColor,
        fontSize: 11,
        interval: 0,
        rotate: data.length > 18 ? 45 : 0
      },
      axisLine: { lineStyle: { color: ruleColor } },
      axisTick: { alignWithLabel: true, lineStyle: { color: ruleColor } }
    },
    yAxis: {
      type: 'value',
      name: '股票数',
      nameTextStyle: { color: textColor, fontSize: 11 },
      axisLabel: { color: textColor, fontSize: 11 },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: ruleColor } }
    },
    series: [
      {
        name: '股票数',
        type: 'bar',
        data: data.map(item => item.count),
        barWidth: '58%',
        itemStyle: {
          color: accentColor,
          borderRadius: [3, 3, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          color: textColor,
          fontSize: 11
        }
      }
    ]
  }
}

function openBucketList (bucket) {
  if (!bucket) return
  router.push({
    name: 'short-term-base-pool',
    query: {
      trade_date: filterForm.value.tradeDate || undefined,
      symbol: filterForm.value.symbol?.trim() || undefined,
      float_market_cap_min: filterForm.value.minMarketCap,
      pct_chg_bucket_key: bucket.bucket_key,
      pct_chg_min: bucket.bucket_start,
      pct_chg_max: bucket.bucket_end,
      pct_chg_label: bucket.label
    }
  })
}

function initChart (data) {
  const chart = init(buildChartOption(data))
  chart?.on('dblclick', params => {
    openBucketList(data[params.dataIndex])
  })
}

async function loadLatestTradeDate () {
  try {
    const date = await tradeCalendarApi.getLatest()
    if (date) {
      filterForm.value.tradeDate = date
      latestTradeDate.value = date
      return date
    }
  } catch (error) {
    console.error('获取最近交易日失败:', error)
  }
  filterForm.value.tradeDate = ''
  latestTradeDate.value = ''
  return ''
}

function getQueryParams () {
  const params = {}
  if (filterForm.value.tradeDate) {
    params.trade_date = filterForm.value.tradeDate
  }
  const trimmedSymbol = filterForm.value.symbol?.trim()
  if (trimmedSymbol) {
    params.symbol = trimmedSymbol
  }
  if (filterForm.value.minMarketCap !== '' && filterForm.value.minMarketCap != null) {
    params.float_market_cap_min = Number(filterForm.value.minMarketCap)
  }
  return params
}

async function loadData () {
  loading.value = true
  try {
    const res = await stockDailyApi.getShortTermPctChgDistribution(getQueryParams())
    if (res?.trade_date) {
      filterForm.value.tradeDate = res.trade_date
    }
    const list = res?.list || []
    total.value = res?.total || 0
    if (list.length) {
      initChart(list)
    } else {
      instance.value?.dispose()
    }
  } catch (error) {
    console.error('加载短线涨幅分布失败:', error)
    total.value = 0
    instance.value?.dispose()
  } finally {
    loading.value = false
  }
}

async function handleSearch () {
  await loadData()
}

async function handleReset () {
  filterForm.value.tradeDate = latestTradeDate.value || ''
  filterForm.value.symbol = ''
  filterForm.value.minMarketCap = 100
  await loadData()
}

onMounted(async () => {
  await loadLatestTradeDate()
  await loadData()
})
</script>

<template>
  <div class="short-term-distribution page">
    <PageHero>
      <template #actions>
        <div class="total-chip">{{ total.toLocaleString() }} 只</div>
      </template>
    </PageHero>

    <StockFilterPanel
      v-model="filterForm"
      :show-market-cap="true"
      :show-turnover="false"
      :show-amount="false"
      :show-board-filters="false"
      @search="handleSearch"
      @reset="handleReset"
    />

    <div class="chart-shell">
      <div ref="chartRef" class="chart-box"></div>
      <div v-if="loading" class="chart-state">加载图表中...</div>
      <div v-else-if="total === 0" class="chart-state">暂无数据</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.short-term-distribution {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100%;
    overflow: visible;
    padding: 4px 16px 12px;
  }
}

.total-chip {
  padding: 6px 12px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 11px;
}

.chart-shell {
  flex: 1;
  min-height: 0;
  position: relative;
  margin-top: 12px;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
}

.chart-box {
  width: 100%;
  height: 100%;
  min-height: 360px;
}

.chart-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-faint);
  font-size: 13px;
  pointer-events: none;
}
</style>
